import React, { useMemo } from 'react';
import { DataTable, ActionsDropdown } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import { Action } from '@aidkitorg/component-library/src/ActionsDropdown';
import { useLocalizedStrings } from '../../../Localization';

export interface ApplicantGroup {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ApplicantGroupsDataTableProps {
  groups: ApplicantGroup[];
  onViewResults: (group: ApplicantGroup) => void;
  onEdit: (group: ApplicantGroup) => void;
  onDelete: (group: ApplicantGroup) => void;
  isLoading?: boolean;
}

export const ApplicantGroupsDataTable: React.FC<ApplicantGroupsDataTableProps> = ({
  groups,
  onViewResults,
  onEdit,
  onDelete,
  isLoading = false,
}) => {
  const L = useLocalizedStrings();
  const strings = L.admin_panel.settings.applicant_groups;

  // Define actions for the hamburger menu
  const actions: Action<ApplicantGroup>[] = useMemo(() => [
    {
      label: strings.view_full_results,
      onClick: onViewResults,
      icon: 'EyeIcon',
    },
    {
      label: strings.edit,
      onClick: onEdit,
      icon: 'PencilIcon',
    },
    {
      label: strings.delete,
      onClick: onDelete,
      icon: 'TrashIcon',
      variant: 'warn',
    },
  ], [strings, onViewResults, onEdit, onDelete]);

  // Define columns for the data table
  const columns = useMemo<ColumnDef<ApplicantGroup>[]>(() => [
    {
      accessorKey: 'name',
      header: strings.group_name,
      enableSorting: true,
      cell: ({ getValue }) => (
        <div className="font-medium text-gray-900">
          {getValue() as string}
        </div>
      ),
    },
    {
      accessorKey: 'description',
      header: strings.group_description,
      enableSorting: false,
      cell: ({ getValue }) => {
        const description = getValue() as string;
        return (
          <div className="text-gray-600 truncate max-w-md">
            {description || '—'}
          </div>
        );
      },
    },
    {
      accessorKey: 'memberCount',
      header: strings.members,
      enableSorting: true,
      cell: ({ getValue }) => {
        const count = getValue() as number;
        return (
          <div className="text-gray-900">
            {count.toLocaleString()}
          </div>
        );
      },
    },
  ], [strings]);

  return (
    <DataTable
      columns={columns}
      data={groups}
      actions={actions}
      enableSearch={true}
      searchLabel={strings.search_groups}
      pageSize={10}
      showPagination={true}
      initializing={isLoading}
      getRowId={(row) => row.id}
    />
  );
};

export default ApplicantGroupsDataTable;
